/**
 * Calculate password strength based on entropy
 * Entropy = log2(charset_size ^ length)
 */

/**
 * Calculate password entropy in bits
 * @param {number} length - Password length
 * @param {object} charTypes - Character types included
 * @returns {number} Entropy in bits
 */
export const calculateEntropy = (length, charTypes) => {
  const {
    lowercase = false,
    uppercase = false,
    numbers = false,
    symbols = false,
  } = charTypes;

  // Calculate total charset size
  let charsetSize = 0;
  if (lowercase) charsetSize += 26;
  if (uppercase) charsetSize += 26;
  if (numbers) charsetSize += 10;
  if (symbols) charsetSize += 32;

  if (charsetSize === 0) return 0;

  // Entropy = log2(charsetSize ^ length)
  const entropy = Math.floor(Math.log2(Math.pow(charsetSize, length)));
  return entropy;
};

/**
 * Get strength level based on entropy
 * @param {number} entropy - Entropy in bits
 * @returns {object} Strength level and color
 */
export const getStrengthLevel = (entropy) => {
  if (entropy < 60) {
    return {
      level: 'Weak',
      color: '#ef4444', // red
      description: 'Consider a longer password or more character types',
    };
  } else if (entropy < 80) {
    return {
      level: 'Strong',
      color: '#eab308', // yellow
      description: 'Good password strength',
    };
  } else {
    return {
      level: 'Very Strong',
      color: '#22c55e', // green
      description: 'Excellent password strength',
    };
  }
};

/**
 * Calculate password strength with level and color
 * @param {number} length - Password length
 * @param {object} charTypes - Character types included
 * @returns {object} Strength info with entropy, level, color, description
 */
export const calculatePasswordStrength = (length, charTypes) => {
  const entropy = calculateEntropy(length, charTypes);
  const strength = getStrengthLevel(entropy);

  return {
    entropy,
    ...strength,
  };
};